import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, Users, Calculator, MoreHorizontal, Clock, FlaskConical, Settings, CreditCard, ShieldCheck, Briefcase, FileCheck } from "lucide-react";
import { useIsAdmin } from "../../hooks/useIsAdmin";

const PRIMARY_NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/invoices", label: "Fatture", icon: FileText, dataTour: "nav-invoices" },
  { path: "/clients", label: "Clienti", icon: Users },
  { path: "/fiscal", label: "Fiscale", icon: Calculator, dataTour: "nav-fiscal" },
];

const MORE_NAV_ITEMS = [
  { path: "/preventivi", label: "Preventivi", icon: FileCheck },
  { path: "/timesheet", label: "Timesheet", icon: Clock },
  { path: "/commercialista", label: "Commercialista", icon: Briefcase },
  { path: "/simulazioni", label: "Simulazioni", icon: FlaskConical, dataTour: "nav-simulazioni" },
  { path: "/abbonamento", label: "Abbonamento", icon: CreditCard },
  { path: "/settings", label: "Profilo", icon: Settings, dataTour: "nav-settings" },
];

export default function BottomNav({ darkMode }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();
  const { isAdmin } = useIsAdmin();

  // Chiudi il menu "Altro" cliccando fuori
  useEffect(() => {
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Voci del menu "Altro": aggiunge "Admin" solo per gli admin (role-based),
  // coerente con la Sidebar desktop (useIsAdmin + icona ShieldCheck).
  const moreItems = isAdmin
    ? [...MORE_NAV_ITEMS, { path: "/admin", label: "Admin", icon: ShieldCheck }]
    : MORE_NAV_ITEMS;

  // Verifica se la route attiva è in "Altro"
  const isMoreActive = moreItems.some((item) => location.pathname === item.path);

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50 md:hidden border-t backdrop-blur transition-colors ${
        darkMode
          ? "bg-neutral-900/95 border-neutral-800"
          : "bg-white/95 border-neutral-200"
      }`}
    >
      <div className="flex items-center justify-around h-16 px-2">
        {PRIMARY_NAV_ITEMS.map(({ path, label, icon: Icon, dataTour }) => (
          <NavLink
            key={path}
            to={path}
            end={path === "/dashboard"}
            data-tour={dataTour}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 py-2 rounded-lg transition-colors ${
                isActive
                  ? darkMode
                    ? "text-forfy-300"
                    : "text-forfy-600"
                  : darkMode
                    ? "text-neutral-500 hover:text-neutral-300"
                    : "text-neutral-500 hover:text-neutral-700"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={`h-5 w-5 ${
                    isActive
                      ? darkMode
                        ? "text-forfy-300"
                        : "text-forfy-600"
                      : ""
                  }`}
                />
                <span
                  className={`text-[10px] mt-1 font-medium ${
                    isActive ? "font-semibold" : ""
                  }`}
                >
                  {label}
                </span>
              </>
            )}
          </NavLink>
        ))}

        {/* Bottone "Altro" */}
        <div className="relative flex-1 flex justify-center" ref={moreRef}>
          <button
            onClick={() => setMoreOpen((v) => !v)}
            className={`flex flex-col items-center justify-center py-2 rounded-lg transition-colors ${
              isMoreActive
                ? darkMode
                  ? "text-forfy-300"
                  : "text-forfy-600"
                : darkMode
                  ? "text-neutral-500 hover:text-neutral-300"
                  : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            <MoreHorizontal
              className={`h-5 w-5 ${
                isMoreActive
                  ? darkMode
                    ? "text-forfy-300"
                    : "text-forfy-600"
                  : ""
              }`}
            />
            <span
              className={`text-[10px] mt-1 font-medium ${
                isMoreActive ? "font-semibold" : ""
              }`}
            >
              Altro
            </span>
          </button>

          {/* Menu popup verso l'alto */}
          {moreOpen && (
            <div
              className={`absolute bottom-full mb-2 right-0 w-48 rounded-xl border shadow-lg py-2 z-50 ${
                darkMode
                  ? "bg-neutral-900 border-neutral-700"
                  : "bg-white border-neutral-200"
              }`}
            >
              {moreItems.map(({ path, label, icon: Icon }) => {
                const isActive = location.pathname === path;
                return (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={() => setMoreOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2.5 text-sm ${
                      isActive
                        ? darkMode
                          ? "text-forfy-300 bg-neutral-800"
                          : "text-forfy-600 bg-forfy-50"
                        : darkMode
                          ? "text-neutral-300 hover:bg-neutral-800"
                          : "text-neutral-700 hover:bg-neutral-50"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Safe area per iPhone con notch */}
      <div className={`h-safe-bottom ${darkMode ? "bg-neutral-900" : "bg-white"}`} />
    </nav>
  );
}
